import { useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import { Button, Card, Input, Modal, Select, Tag } from '@/shared/ui/primitives';
import EntityTable from '@/shared/ui/EntityTable';
import { tEnum } from '@/shared/utils/enumTranslation';
import { useAdminUserList, useCreateAdminUser, useDeleteAdminUser } from '../hooks/useAdminUsers';
import type { AdminRole, AdminUser, CreateAdminUserInput } from '../api/adminUser.api';

const ROLES: AdminRole[] = ['SUPER_ADMIN', 'ADMIN', 'MODERATOR', 'SUPPORT'];

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const params = useMemo(() => ({ page, size: 20, sort: ['id,asc'] }), [page]);
  const { data, isLoading } = useAdminUserList(params);
  const createMutation = useCreateAdminUser();
  const deleteMutation = useDeleteAdminUser();

  const rows = useMemo(() => {
    const all = data?.content ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((admin) => [
      admin.email,
      admin.firstName,
      admin.lastName,
      tEnum(t, 'adminRole', admin.role),
    ].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [data?.content, search, t]);

  return (
    <div>
      <PageHeader
        title={t('adminUsers.title')}
        subtitle={t('adminUsers.subtitle')}
        actionLabel={t('adminUsers.create')}
        onAction={() => setCreateOpen(true)}
      />

      <Card style={{ marginBottom: 16 }}>
        <div className="ds-row" style={{ gap: 12, alignItems: 'center' }}>
          <Input
            value={search}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
            placeholder={t('adminUsers.searchPlaceholder')}
            style={{ minWidth: 280 }}
          />
          <div className="ds-muted">{t('adminUsers.count', { count: data?.totalItems ?? 0 })}</div>
        </div>
      </Card>

      <EntityTable<AdminUser>
        data={rows}
        loading={isLoading}
        page={data?.currentPage ?? page}
        pageSize={data?.pageSize ?? 20}
        total={data?.totalItems ?? 0}
        onPageChange={setPage}
        columns={[
          {
            title: t('adminUsers.columns.admin'),
            render: (_: any, row: AdminUser) => (
              <div>
                <strong>{[row.firstName, row.lastName].filter(Boolean).join(' ') || row.email}</strong>
                <div className="ds-muted">{row.email}</div>
              </div>
            ),
          },
          { title: t('adminUsers.columns.role'), dataIndex: 'role', render: (role: AdminRole) => tEnum(t, 'adminRole', role) },
          {
            title: t('adminUsers.columns.status'),
            dataIndex: 'isActive',
            render: (active: boolean) => <Tag color={active === false ? 'default' : 'success'}>{active === false ? t('common.inactive') : t('common.active')}</Tag>,
          },
          { title: t('adminUsers.columns.lastLogin'), dataIndex: 'lastLogin', render: (value: string) => value ? value.slice(0, 10) : t('common.notFound') },
          {
            title: t('common.actions'),
            render: (_: any, row: AdminUser) => (
              <Button size="small" danger onClick={() => setDeleteTarget(row)}>
                {t('common.delete')}
              </Button>
            ),
          },
        ]}
      />

      <CreateAdminDialog
        open={createOpen}
        loading={createMutation.isPending}
        onCancel={() => setCreateOpen(false)}
        onSubmit={async (input) => {
          await createMutation.mutateAsync(input);
          setCreateOpen(false);
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('adminUsers.deleteTitle')}
        message={deleteTarget ? t('adminUsers.deleteMessage', { email: deleteTarget.email }) : ''}
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
        }}
      />
    </div>
  );
}

function CreateAdminDialog({
  open,
  loading,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (input: CreateAdminUserInput) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateAdminUserInput>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'ADMIN',
  });

  const update = (key: keyof CreateAdminUserInput, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  return (
    <Modal
      open={open}
      title={t('adminUsers.create')}
      onCancel={onCancel}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <Button type="primary" loading={loading} onClick={() => onSubmit(form)}>
            {t('common.create')}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <Input value={form.firstName} onChange={(e: ChangeEvent<HTMLInputElement>) => update('firstName', e.target.value)} placeholder={t('adminUsers.fields.firstName')} />
        <Input value={form.lastName} onChange={(e: ChangeEvent<HTMLInputElement>) => update('lastName', e.target.value)} placeholder={t('adminUsers.fields.lastName')} />
        <Input value={form.email} onChange={(e: ChangeEvent<HTMLInputElement>) => update('email', e.target.value)} placeholder={t('adminUsers.fields.email')} />
        <Input.Password value={form.password} onChange={(e: ChangeEvent<HTMLInputElement>) => update('password', e.target.value)} placeholder={t('adminUsers.fields.password')} />
        <Select
          value={form.role}
          onChange={(value) => update('role', String(value))}
          options={ROLES.map((role) => ({ value: role, label: tEnum(t, 'adminRole', role) }))}
          style={{ width: '100%' }}
        />
      </div>
    </Modal>
  );
}
